/**
 * @fileoverview Case Ingestion Assist API Service - RTK Query endpoints for CMS import.
 *
 * Endpoints:
 * - GET /api/matters/:id/case-ingestion/import/ - Fetch import artifact
 * - POST /api/matters/:id/case-ingestion/import/ - Create import artifact (one per matter)
 */
import { createApi } from '@reduxjs/toolkit/query/react';

import type {
  CaseIngestionImport,
  CreateCaseIngestionImportRequest,
} from '../types/case-ingestion-assist-types';
import { baseQueryWithErrorHandling } from './baseQuery';

export const caseIngestionAssistApiClient = createApi({
  reducerPath: 'caseIngestionAssistApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['CaseIngestionImport'] as const,
  endpoints: (builder) => ({
    /** Get the import artifact for a matter (returns null when 404 / no import exists) */
    getCaseIngestionImport: builder.query<CaseIngestionImport | null, string>({
      queryFn: async (matterId, _queryApi, _extraOptions, baseQuery) => {
        const result = await baseQuery(`api/matters/${matterId}/case-ingestion/import/`);
        if (result.error) {
          if (result.error.status === 404) {
            return { data: null };
          }
          return { error: result.error };
        }
        return { data: result.data as CaseIngestionImport };
      },
      providesTags: (_result, _error, matterId) => [
        { type: 'CaseIngestionImport', id: matterId },
      ],
    }),

    /** Create the import artifact for a matter (one-time, rejects if already exists) */
    createCaseIngestionImport: builder.mutation<
      CaseIngestionImport,
      { matterId: string; data: CreateCaseIngestionImportRequest }
    >({
      query: ({ matterId, data }) => ({
        url: `api/matters/${matterId}/case-ingestion/import/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'CaseIngestionImport', id: matterId },
      ],
    }),
  }),
});

export const {
  useGetCaseIngestionImportQuery,
  useCreateCaseIngestionImportMutation,
} = caseIngestionAssistApiClient;
